import { HOST_ID } from './constants';
import { formatCode, formatCountdown } from './format';
import { STORAGE_STATUS_POSITION } from './constants';
import { de } from './i18n';
import { STATUS_POSITIONS, type StatusPosition } from './options';
import { storageGet, storageSet } from './storage';

/**
 * Oberfläche des SDK: Code-Modal, Consent-Modal, Statusleiste, Steuerungs-Modal und
 * Annotations-Ebene in einem offenen Shadow DOM (Spec Kap. 5 Punkt 2, 3, 5, 6, 7). Keine
 * Framework-Abhängigkeit, kein `innerHTML` mit Fremdtexten, keine `style`-Attribute
 * (CSP-freundlich; Styles per `adoptedStyleSheets` bzw. `<style nonce>`, Positionen per CSSOM).
 *
 * Aufbau: `#relyo-root` (Host, per `blockSelector` geblockt) → Shadow Root → `<div
 * data-relyo="container">` → zweiter Shadow Root mit Styles und allen Elementen. rrweb beobachtet
 * den ersten Shadow Root eines serialisierten Hosts (MutationObserver und `adoptedStyleSheets`,
 * auch bei geblockten Hosts). Der innere Shadow Root wird angelegt, solange der Host noch nicht im
 * Dokument hängt; rrweb sieht ihn deshalb nie – weder Styles (früher als `StyleSheetRule` im
 * Stream) noch später eingefügte Elemente wie Pointer oder Steuerungs-Modal.
 */

const MAX_Z = '2147483647';

const CSS = `
:host { all: initial; }
* { box-sizing: border-box; }
.relyo-backdrop {
  position: fixed; inset: 0; pointer-events: auto;
  background: rgba(15, 23, 42, 0.55);
  display: flex; align-items: center; justify-content: center; padding: 16px;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}
.relyo-modal {
  background: #fff; color: #0f172a; border-radius: 12px; width: 100%; max-width: 420px;
  padding: 24px; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3); font-size: 15px; line-height: 1.5;
}
.relyo-modal h2 { margin: 0 0 12px; font-size: 19px; line-height: 1.3; font-weight: 650; }
.relyo-modal p { margin: 0 0 12px; }
.relyo-code {
  font-size: 44px; font-weight: 700; letter-spacing: 0.08em; text-align: center;
  font-variant-numeric: tabular-nums; margin: 8px 0 4px; color: #0f172a; white-space: nowrap;
}
.relyo-muted { color: #475569; font-size: 14px; text-align: center; }
.relyo-error { color: #b91c1c; }
.relyo-list { margin: 0 0 12px; padding: 0; list-style: none; }
.relyo-list li { margin: 0 0 6px; }
.relyo-list strong { font-weight: 600; }
.relyo-note { background: #fef3c7; color: #78350f; border-radius: 8px; padding: 8px 12px; }
.relyo-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px; flex-wrap: wrap; }
.relyo-btn {
  font: inherit; font-size: 15px; font-weight: 600; border-radius: 8px; padding: 9px 16px;
  cursor: pointer; border: 1px solid #cbd5e1; background: #fff; color: #0f172a;
}
.relyo-btn:hover { background: #f1f5f9; }
.relyo-btn:focus-visible { outline: 3px solid #93c5fd; outline-offset: 1px; }
.relyo-btn-primary { background: #2563eb; border-color: #2563eb; color: #fff; }
.relyo-btn-primary:hover { background: #1d4ed8; }
/* Rahmen um das Fenster: zeigt die laufende Session, ohne etwas zu verdecken (Issue #9). */
.relyo-frame {
  position: fixed; inset: 0; pointer-events: none; border: 3px solid #b91c1c;
}
.relyo-frame[data-state="reconnecting"] { border-color: #a16207; }
/* Status-Pille: klein, verschiebbar, rastet an sechs Positionen ein. */
.relyo-bar {
  position: fixed; pointer-events: auto; width: max-content; max-width: calc(100vw - 24px);
  display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 8px;
  padding: 6px 6px 6px 14px; border-radius: 22px;
  background: #b91c1c; color: #fff; font-size: 13px; font-weight: 600; line-height: 1.3;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  cursor: grab; touch-action: none; user-select: none; -webkit-user-select: none;
}
.relyo-bar[data-dragging] { cursor: grabbing; }
.relyo-bar[data-pos^="bottom"] { bottom: calc(12px + env(safe-area-inset-bottom, 0px)); }
.relyo-bar[data-pos^="top"] { top: calc(12px + env(safe-area-inset-top, 0px)); }
.relyo-bar[data-pos="bottom"], .relyo-bar[data-pos="top"] { left: 50%; transform: translateX(-50%); }
.relyo-bar[data-pos$="-left"] { left: 12px; }
.relyo-bar[data-pos$="-right"] { right: 12px; }
.relyo-bar[data-state="reconnecting"] { background: #a16207; }
.relyo-bar-dot { width: 8px; height: 8px; border-radius: 50%; background: #fff; flex: none; }
.relyo-bar-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.relyo-bar .relyo-btn {
  padding: 4px 12px; font-size: 13px; border-radius: 16px;
  background: #fff; color: #b91c1c; border-color: #fff;
}
.relyo-bar-control {
  background: #fff; color: #b91c1c; border-radius: 999px; padding: 2px 10px; font-size: 12px;
  white-space: nowrap;
}
.relyo-modal-small { max-width: 360px; padding: 20px; font-size: 14px; }
.relyo-modal-small h2 { font-size: 17px; }
.relyo-overlay { position: fixed; inset: 0; pointer-events: none; overflow: hidden; }
.relyo-canvas { position: absolute; left: 0; top: 0; pointer-events: none; }
.relyo-pointer {
  position: absolute; left: 0; top: 0; width: 14px; height: 14px; margin: -7px 0 0 -7px;
  border-radius: 50%; background: #dc2626; border: 2px solid #fff; pointer-events: none;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}
.relyo-pointer-label {
  position: absolute; left: 16px; top: -4px; white-space: nowrap; pointer-events: none;
  background: #dc2626; color: #fff; border-radius: 4px; padding: 1px 6px; font-size: 12px;
  font-weight: 600; font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}
`;

export interface UiCallbacks {
  onCodeCancel(): void;
  onCodeRetry(): void;
  onCodeExpired(): void;
  onConsent(granted: boolean): void;
  onEnd(): void;
  onControlAnswer(granted: boolean): void;
  onControlStop(): void;
}

export interface ConsentView {
  agentName: string;
  recording: boolean;
  consentText: string | null;
}

type Attrs = Record<string, string>;

function h(tag: string, attrs: Attrs = {}, ...children: (Node | string)[]): HTMLElement {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') el.className = v;
    else el.setAttribute(k, v);
  }
  for (const c of children) el.append(c);
  return el;
}

/**
 * Knopf der SDK-Oberfläche. Reagiert nur auf echte Klicks (`isTrusted`, Konzept Abschnitt 3a):
 * Ein eingeschleustes Skript auf der Seite kann so weder Freigabe noch Steuerung bestätigen –
 * auch nicht ablehnen oder beenden, damit das Verhalten für alle Knöpfe gleich ist. Tastatur
 * (Enter/Leertaste auf dem fokussierten Knopf) erzeugt ebenfalls einen echten Klick.
 */
function button(label: string, hook: string, primary: boolean, onClick: () => void): HTMLElement {
  const b = h(
    'button',
    {
      type: 'button',
      class: primary ? 'relyo-btn relyo-btn-primary' : 'relyo-btn',
      'data-relyo': hook,
    },
    label,
  );
  b.addEventListener('click', (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (ev.isTrusted !== true) return;
    onClick();
  });
  return b;
}

function focusPrimary(el: HTMLElement): void {
  const primary =
    el.querySelector<HTMLElement>('.relyo-btn-primary') ?? el.querySelector<HTMLElement>('button');
  try {
    primary?.focus();
  } catch {
    // Fokus ist Komfort, kein Muss.
  }
}

/** Wächter (#40): mehr als so viele Wiederherstellungen im Zeitfenster beenden die Session. */
const GUARD_MAX_RESTORES = 10;
const GUARD_WINDOW_MS = 2_000;

export class RelyoUi {
  private host: HTMLElement | null = null;
  private root: ShadowRoot | null = null;
  private modal: HTMLElement | null = null;
  private controlModal: HTMLElement | null = null;
  private bar: HTMLElement | null = null;
  private frame: HTMLElement | null = null;
  private overlayEl: HTMLElement | null = null;
  private countdown: ReturnType<typeof setInterval> | null = null;
  private guard: MutationObserver | null = null;
  private restores: number[] = [];

  constructor(
    private readonly cb: UiCallbacks,
    private readonly nonce: string | null,
    private readonly statusPosition: StatusPosition = 'bottom',
  ) {}

  /** Shadow Root (für Tests); `null`, solange nichts angezeigt wird. */
  get shadow(): ShadowRoot | null {
    return this.root;
  }

  get hasModal(): boolean {
    return this.modal !== null;
  }

  /**
   * `true`, wenn das Ereignis aus der SDK-Oberfläche stammt (z. B. Klick auf „Steuerung
   * beenden“). Solche Eingaben sind kein Nutzer-Interrupt der Fernsteuerung.
   */
  isOwnEvent(ev: Event): boolean {
    const host = this.host;
    if (!host) return false;
    try {
      if (ev.composedPath().includes(host)) return true;
    } catch {
      // composedPath fehlt: Fallback unten
    }
    return ev.target instanceof Node && host.contains(ev.target);
  }

  /** `true`, wenn der Knoten zur SDK-Oberfläche gehört (Host-Element oder darin). */
  isOwnNode(node: Node): boolean {
    const host = this.host;
    return !!host && (node === host || host.contains(node));
  }

  // ------------------------------------------------------------------ Code

  showCodeLoading(): void {
    this.setModal(
      this.modalCard(
        'code-modal',
        de.codeTitle,
        h('p', { class: 'relyo-muted', 'data-relyo': 'code-loading' }, de.codeLoading),
        [button(de.cancel, 'code-cancel', false, () => this.cb.onCodeCancel())],
      ),
    );
  }

  /** `expiresAtLocal`: Ablaufzeit in lokaler Uhrzeit (ms epoch, bereits um Server-Offset korrigiert). */
  showCode(code: string, expiresAtLocal: number): void {
    const timerText = h('p', { class: 'relyo-muted', 'data-relyo': 'code-countdown' });
    const update = (): boolean => {
      const rest = expiresAtLocal - Date.now();
      timerText.textContent = de.codeValid(formatCountdown(rest));
      return rest > 0;
    };
    this.setModal(
      this.modalCard(
        'code-modal',
        de.codeTitle,
        [
          h('p', {}, de.codeIntro),
          h(
            'div',
            { class: 'relyo-code', 'data-relyo': 'code', 'aria-live': 'polite' },
            formatCode(code),
          ),
          timerText,
        ],
        [button(de.cancel, 'code-cancel', false, () => this.cb.onCodeCancel())],
      ),
    );
    if (!update()) {
      this.cb.onCodeExpired();
      return;
    }
    this.countdown = setInterval(() => {
      if (!update()) {
        this.stopCountdown();
        this.cb.onCodeExpired();
      }
    }, 1000);
  }

  showCodeExpired(): void {
    this.setModal(
      this.modalCard(
        'code-modal',
        de.codeTitle,
        h('p', { 'data-relyo': 'code-expired' }, de.codeExpired),
        [
          button(de.close, 'code-cancel', false, () => this.cb.onCodeCancel()),
          button(de.codeNew, 'code-retry', true, () => this.cb.onCodeRetry()),
        ],
      ),
    );
  }

  showCodeError(message: string): void {
    this.setModal(
      this.modalCard(
        'code-modal',
        de.codeTitle,
        h('p', { class: 'relyo-error', 'data-relyo': 'code-error' }, message),
        [button(de.close, 'code-cancel', false, () => this.cb.onCodeCancel())],
      ),
    );
  }

  // --------------------------------------------------------------- Consent

  showConsent(v: ConsentView): void {
    const intro = v.consentText
      ? v.consentText.split('{agent}').join(v.agentName)
      : de.consentIntro(v.agentName);
    const body: Node[] = [
      h('p', {}, intro),
      h(
        'ul',
        { class: 'relyo-list' },
        h('li', {}, h('strong', {}, de.consentShared), ' ', de.consentSharedWhat),
        h('li', {}, h('strong', {}, de.consentNotShared), ' ', de.consentNotSharedWhat),
      ),
    ];
    if (v.recording) {
      body.push(
        h('p', { class: 'relyo-note', 'data-relyo': 'consent-recording' }, de.consentRecording),
      );
    }
    this.setModal(
      this.modalCard('consent', de.consentTitle(v.agentName), body, [
        button(de.decline, 'consent-decline', false, () => this.cb.onConsent(false)),
        button(de.accept, 'consent-accept', true, () => this.cb.onConsent(true)),
      ]),
    );
  }

  closeModal(): void {
    this.stopCountdown();
    this.modal?.remove();
    this.modal = null;
    this.maybeRemoveHost();
  }

  // ------------------------------------------------------- Remote Control

  /** Zweites, kleineres Modal für die Steuerungsanfrage (Spec Kap. 5 Punkt 7, Kap. 9). */
  showControlRequest(agentName: string): void {
    this.controlModal?.remove();
    const el = this.modalCard(
      'control',
      de.controlTitle(agentName),
      h('p', { 'data-relyo': 'control-hint' }, de.controlHint),
      [
        button(de.decline, 'control-decline', false, () => this.cb.onControlAnswer(false)),
        button(de.controlAccept, 'control-accept', true, () => this.cb.onControlAnswer(true)),
      ],
      true,
    );
    this.ensureRoot().append(el);
    this.controlModal = el;
    focusPrimary(el);
  }

  closeControlRequest(): void {
    this.controlModal?.remove();
    this.controlModal = null;
    this.maybeRemoveHost();
  }

  /** Zeigt in der Statusleiste „{Agent} steuert gerade“ + „Steuerung beenden“ (`null`: aus). */
  setControlActive(agentName: string | null): void {
    const bar = this.bar;
    if (!bar) return;
    bar.querySelector('[data-relyo="control-active"]')?.remove();
    bar.querySelector('[data-relyo="control-stop"]')?.remove();
    if (agentName === null) return;
    const end = bar.querySelector('[data-relyo="end"]');
    const badge = h(
      'span',
      { class: 'relyo-bar-control', 'data-relyo': 'control-active' },
      de.controlActive(agentName),
    );
    const stop = button(de.controlStop, 'control-stop', false, () => this.cb.onControlStop());
    bar.insertBefore(badge, end);
    bar.insertBefore(stop, end);
  }

  // ---------------------------------------------------------- Annotationen

  /**
   * Annotations-Ebene über dem ganzen Viewport (`position: fixed; inset: 0;
   * pointer-events: none`). Liegt im Shadow Root vor Modals und Statusleiste und damit darunter.
   */
  overlay(): HTMLElement {
    const root = this.ensureRoot();
    if (!this.overlayEl) {
      this.overlayEl = h('div', { class: 'relyo-overlay', 'data-relyo': 'overlay' });
    }
    if (this.overlayEl.parentNode !== root) root.prepend(this.overlayEl);
    return this.overlayEl;
  }

  removeOverlay(): void {
    this.overlayEl?.remove();
    this.overlayEl = null;
    this.maybeRemoveHost();
  }

  // ------------------------------------------------------------ Statusleiste

  /**
   * Statusanzeige während der Session (Issue #9): roter Rahmen um das Fenster (klickt man
   * durch) plus kleine Pille mit Text und „Beenden“. Die Pille lässt sich verschieben und rastet
   * an der nächsten von sechs Positionen ein; die Wahl gilt pro Browser.
   */
  showStatus(agentName: string, state: 'live' | 'reconnecting'): void {
    const root = this.ensureRoot();
    const text = state === 'live' ? de.statusLive(agentName) : de.statusReconnecting;
    if (!this.frame) {
      this.frame = h('div', { class: 'relyo-frame', 'data-relyo': 'statusframe' });
      root.append(this.frame);
    }
    if (!this.bar) {
      this.bar = h(
        'div',
        {
          class: 'relyo-bar',
          role: 'status',
          'data-relyo': 'statusbar',
          'data-pos': this.initialPosition(),
          title: de.statusMove,
        },
        h('span', { class: 'relyo-bar-dot', 'aria-hidden': 'true' }),
        h('span', { class: 'relyo-bar-text', 'data-relyo': 'statusbar-text' }),
        button(de.end, 'end', false, () => this.cb.onEnd()),
      );
      this.makeDraggable(this.bar);
      root.append(this.bar);
    }
    this.frame.setAttribute('data-state', state);
    this.bar.setAttribute('data-state', state);
    const span = this.bar.querySelector('[data-relyo="statusbar-text"]');
    if (span) span.textContent = text;
  }

  hideStatus(): void {
    this.bar?.remove();
    this.bar = null;
    this.frame?.remove();
    this.frame = null;
    this.maybeRemoveHost();
  }

  destroy(): void {
    this.stopCountdown();
    this.stopGuard();
    this.modal = null;
    this.controlModal = null;
    this.bar = null;
    this.frame = null;
    this.overlayEl = null;
    this.host?.remove();
    this.host = null;
    this.root = null;
  }

  // ---------------------------------------------------------------- intern

  private initialPosition(): StatusPosition {
    const stored = storageGet('local', STORAGE_STATUS_POSITION);
    return STATUS_POSITIONS.includes(stored as StatusPosition)
      ? (stored as StatusPosition)
      : this.statusPosition;
  }

  /**
   * Ziehen per Maus oder Finger (Pointer Events, nur echte Eingaben). Knöpfe in der Pille bleiben
   * normale Knöpfe. Während des Ziehens folgt die Pille dem Zeiger; beim Loslassen rastet sie an
   * der nächsten Position ein (oben/unten × links/Mitte/rechts).
   */
  private makeDraggable(bar: HTMLElement): void {
    let start: { x: number; y: number; left: number; top: number; id: number } | null = null;
    let moved = false;
    bar.addEventListener('pointerdown', (ev) => {
      if (ev.isTrusted !== true || ev.button !== 0) return;
      if (ev.target instanceof Element && ev.target.closest('button')) return;
      const rect = bar.getBoundingClientRect();
      start = { x: ev.clientX, y: ev.clientY, left: rect.left, top: rect.top, id: ev.pointerId };
      moved = false;
      try {
        bar.setPointerCapture(ev.pointerId);
      } catch {
        // ohne Capture endet das Ziehen spätestens beim nächsten pointerup
      }
      ev.preventDefault();
    });
    bar.addEventListener('pointermove', (ev) => {
      if (!start || ev.pointerId !== start.id) return;
      const dx = ev.clientX - start.x;
      const dy = ev.clientY - start.y;
      if (!moved && Math.hypot(dx, dy) < 4) return;
      if (!moved) {
        moved = true;
        bar.setAttribute('data-dragging', '');
        bar.setAttribute('data-pos', 'free');
      }
      const s = bar.style;
      s.setProperty('left', `${start.left + dx}px`);
      s.setProperty('top', `${start.top + dy}px`);
    });
    const finish = (ev: PointerEvent) => {
      if (!start || ev.pointerId !== start.id) return;
      start = null;
      if (!moved) return;
      const rect = bar.getBoundingClientRect();
      const pos = snapPosition(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
        window.innerWidth,
        window.innerHeight,
      );
      bar.removeAttribute('data-dragging');
      bar.style.removeProperty('left');
      bar.style.removeProperty('top');
      bar.setAttribute('data-pos', pos);
      storageSet('local', STORAGE_STATUS_POSITION, pos);
    };
    bar.addEventListener('pointerup', finish);
    bar.addEventListener('pointercancel', finish);
  }

  private modalCard(
    hook: string,
    title: string,
    body: Node | Node[],
    actions: HTMLElement[],
    small = false,
  ): HTMLElement {
    const titleId = `relyo-title-${hook}`;
    const card = h(
      'div',
      {
        class: small ? 'relyo-modal relyo-modal-small' : 'relyo-modal',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': titleId,
        'data-relyo': hook,
      },
      h('h2', { id: titleId }, title),
    );
    card.append(...(Array.isArray(body) ? body : [body]));
    card.append(h('div', { class: 'relyo-actions' }, ...actions));
    return h('div', { class: 'relyo-backdrop' }, card);
  }

  private setModal(el: HTMLElement): void {
    this.stopCountdown();
    this.modal?.remove();
    const root = this.ensureRoot();
    root.append(el);
    this.modal = el;
    focusPrimary(el);
  }

  private stopCountdown(): void {
    if (this.countdown !== null) clearInterval(this.countdown);
    this.countdown = null;
  }

  private ensureRoot(): ShadowRoot {
    if (this.root && this.host?.isConnected) return this.root;
    this.host?.remove();
    const host = document.createElement('div');
    host.id = HOST_ID;
    host.setAttribute('data-relyo', 'root');
    const s = host.style;
    s.setProperty('position', 'fixed', 'important');
    s.setProperty('inset', '0', 'important');
    s.setProperty('z-index', MAX_Z, 'important');
    s.setProperty('pointer-events', 'none', 'important');
    s.setProperty('display', 'block', 'important');
    // Beide Shadow Roots entstehen, bevor der Host ins Dokument kommt: rrwebs gepatchtes
    // `attachShadow` meldet nur Roots von Elementen, die bereits im Dokument hängen.
    const outer = host.attachShadow({ mode: 'open' });
    const container = document.createElement('div');
    container.setAttribute('data-relyo', 'container');
    // `rr-block` ist rrwebs Standard-Block-Klasse – zweite Sicherung, falls der Container doch
    // einmal serialisiert würde.
    container.className = 'rr-block';
    const root = container.attachShadow({ mode: 'open' });
    outer.append(container);
    this.applyStyles(root);
    // Bereits angezeigte Elemente (z. B. nach Entfernen des Hosts durch die Host-App) neu einhängen.
    if (this.overlayEl) root.append(this.overlayEl);
    if (this.modal) root.append(this.modal);
    if (this.controlModal) root.append(this.controlModal);
    if (this.frame) root.append(this.frame);
    if (this.bar) root.append(this.bar);
    (document.body ?? document.documentElement).append(host);
    this.host = host;
    this.root = root;
    this.startGuard();
    return root;
  }

  /**
   * Issue #40: Frameworks, die `body` austauschen oder morphen (Blazor Enhanced Navigation,
   * Turbo, htmx-Boost), entfernen den Host. Die Übertragung liefe weiter, ohne dass der Nutzer
   * es sieht. Der Wächter hängt die Oberfläche sofort wieder ein; wird sie in kurzer Zeit
   * immer wieder entfernt, endet die Session, statt unsichtbar weiterzulaufen.
   */
  private startGuard(): void {
    if (this.guard || typeof MutationObserver !== 'function') return;
    this.guard = new MutationObserver(() => this.checkHost());
    this.guard.observe(document.documentElement, { childList: true, subtree: true });
  }

  private stopGuard(): void {
    this.guard?.disconnect();
    this.guard = null;
    this.restores = [];
  }

  private checkHost(): void {
    if (!this.host || this.host.isConnected) return;
    const now = Date.now();
    this.restores = this.restores.filter((t) => now - t < GUARD_WINDOW_MS);
    this.restores.push(now);
    if (this.restores.length > GUARD_MAX_RESTORES) {
      this.stopGuard();
      console.warn('[Relyo] Statusanzeige wird wiederholt entfernt – Session beendet.');
      this.cb.onEnd();
      return;
    }
    this.ensureRoot();
  }

  private applyStyles(root: ShadowRoot): void {
    // Mit Nonce: klassisches <style nonce>. Ohne Nonce: Constructable Stylesheet, das von
    // `style-src` nicht als Inline-Style behandelt wird; Fallback <style>.
    try {
      if (!this.nonce && 'adoptedStyleSheets' in root && typeof CSSStyleSheet === 'function') {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(CSS);
        root.adoptedStyleSheets = [sheet];
        return;
      }
    } catch {
      // Fallback unten
    }
    const style = document.createElement('style');
    if (this.nonce) style.nonce = this.nonce;
    style.textContent = CSS;
    root.append(style);
  }

  private maybeRemoveHost(): void {
    if (this.modal || this.controlModal || this.bar || this.frame || this.overlayEl) return;
    this.stopGuard();
    this.host?.remove();
    this.host = null;
    this.root = null;
  }
}

/** Nächste Raststelle für den Mittelpunkt der Pille: oben/unten × linkes, mittleres, rechtes Drittel. */
export function snapPosition(x: number, y: number, width: number, height: number): StatusPosition {
  const v = y < height / 2 ? 'top' : 'bottom';
  if (x < width / 3) return `${v}-left`;
  if (x > (width * 2) / 3) return `${v}-right`;
  return v;
}
